import React, { useEffect, useState } from "react";
import { apiGet, apiPost, formatDateTime } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { RefreshCcw, TrendingUp, Activity, CheckCircle2, AlertTriangle, BarChart3 } from "lucide-react";
import { toast } from "sonner";

/**
 * TradingView data control panel — visible on the System Status page.
 *
 * Shows coverage (X/Y stocks have TV fundamentals · technicals) plus two
 * trigger buttons. Polls `/admin/tv-stats` every 8 seconds while a job is
 * running so the admin sees live progress.
 */
const TradingViewCard = () => {
  const [stats, setStats] = useState(null);
  const [running, setRunning] = useState({ fund: false, tech: false });

  const load = () => apiGet("/admin/tv-stats").then(setStats).catch(() => {});

  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, []);

  const triggerFundamentals = async () => {
    setRunning((s) => ({ ...s, fund: true }));
    try {
      const res = await apiPost("/admin/refresh-tv-fundamentals");
      if (res?.ok) {
        toast.success("TradingView fundamentals refreshed", {
          description: res?.message || `${res.updated}/${res.matched} updated`,
          duration: 9000,
        });
      } else {
        toast.error(res?.error || "TV fundamentals refresh failed");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally { setRunning((s) => ({ ...s, fund: false })); }
  };

  const triggerTechnicals = async () => {
    setRunning((s) => ({ ...s, tech: true }));
    try {
      const res = await apiPost("/admin/refresh-tv-technicals");
      if (res?.status === "already_running") {
        toast.info(`Already running (${res?.progress?.processed || 0}/${res?.progress?.total || 0})`);
      } else {
        toast.success("Technical signals refresh started", {
          description: "Polling progress every 8 seconds. ~145 seconds total for 287 stocks.",
        });
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally { setRunning((s) => ({ ...s, tech: false })); }
  };

  const total = stats?.total_active_securities || 0;
  const fundOk = stats?.with_tv_fundamentals_today || 0;
  const techOk = stats?.with_tv_technicals_today || 0;
  const techJob = stats?.technicals_status;
  const techRunning = techJob?.status === "running";
  const techProgress = techJob?.total ? Math.round((techJob.processed / techJob.total) * 100) : 0;
  const fundJob = stats?.fundamentals_status;

  return (
    <Card className="border bg-card" data-testid="tradingview-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-display flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-primary" />
          TradingView Data
        </CardTitle>
        <CardDescription>
          Free CSE fundamentals (PE / EPS / yield / ROE) and daily technical signals (BUY / SELL / RSI / MACD)
          via TradingView's screener and TA endpoints.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Coverage stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-lg border bg-muted/30 p-3" data-testid="tv-fundamentals-coverage">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Fundamentals coverage</span>
              {fundOk === total && total > 0 ? (
                <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" />
              )}
            </div>
            <div className="text-2xl font-display font-semibold mt-1 tabular">
              {fundOk}<span className="text-muted-foreground">/{total}</span>
            </div>
            <div className="text-[11px] text-muted-foreground">
              Last refresh: {fundJob?.completed_at ? formatDateTime(fundJob.completed_at) : "—"}
            </div>
            {fundJob?.overrides ? (
              <Badge variant="secondary" className="mt-2 text-[10px]">
                {fundJob.overrides} mock overrides preserved
              </Badge>
            ) : null}
          </div>

          <div className="rounded-lg border bg-muted/30 p-3" data-testid="tv-technicals-coverage">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Technical signals coverage</span>
              {techRunning ? (
                <Badge variant="outline" className="text-[10px] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40">
                  running
                </Badge>
              ) : techOk > 0 ? (
                <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
            <div className="text-2xl font-display font-semibold mt-1 tabular">
              {techOk}<span className="text-muted-foreground">/{total}</span>
            </div>
            <div className="text-[11px] text-muted-foreground">
              Last refresh: {techJob?.completed_at ? formatDateTime(techJob.completed_at) : "—"}
            </div>
            {techRunning && (
              <div className="mt-2 space-y-1">
                <Progress value={techProgress} className="h-1.5" />
                <div className="text-[10px] text-muted-foreground">
                  {techJob?.processed || 0} / {techJob?.total || 0} processed · {techJob?.failed || 0} failed
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Trigger buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Button
            onClick={triggerFundamentals}
            disabled={running.fund}
            className="gap-2 justify-start"
            data-testid="refresh-tv-fundamentals-button"
          >
            <TrendingUp className={`h-4 w-4 ${running.fund ? "animate-pulse" : ""}`} />
            Refresh TV Fundamentals
          </Button>
          <Button
            onClick={triggerTechnicals}
            disabled={running.tech || techRunning}
            variant="secondary"
            className="gap-2 justify-start"
            data-testid="refresh-tv-technicals-button"
          >
            <Activity className={`h-4 w-4 ${(running.tech || techRunning) ? "animate-pulse" : ""}`} />
            Refresh TV Technicals
          </Button>
        </div>

        {/* Last fundamentals message inline */}
        {fundJob?.message && (
          <div
            className="rounded-md border bg-card px-3 py-2 text-[11px] text-muted-foreground whitespace-pre-line"
            data-testid="tv-fundamentals-message"
          >
            <span className="font-semibold text-foreground">Last fundamentals run:</span>
            {"\n"}
            {fundJob.message}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TradingViewCard;
